import { createSlice } from "@reduxjs/toolkit"
import { createAppAsyncThunk } from "app/hooks"
import scheduleAPI from "./scheduleAPI"
import { resetStoreAction } from "config"
import { IScheduleSliceState } from "./scheduleSlice.types"
import {
  ICreateSchedulePayload,
  IUpdateSchedulePayload,
} from "model/food/types/schedulePayloads.types"

export const getSchedules = createAppAsyncThunk(
  "schedule/getSchedules",
  async (_, { rejectWithValue }) => {
    try {
      const schedules = await scheduleAPI.getSchedules()
      return schedules
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const getScheduleDetails = createAppAsyncThunk(
  "schedule/getScheduleDetails",
  async (scheduleId: string, { rejectWithValue }) => {
    try {
      const scheduleDetails = await scheduleAPI.getSchedule(scheduleId)
      return scheduleDetails
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const createSchedule = createAppAsyncThunk(
  "schedule/createSchedule",
  async (payload: ICreateSchedulePayload, { rejectWithValue }) => {
    try {
      const meal = await scheduleAPI.createSchedule(payload)
      return meal
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const updateSchedule = createAppAsyncThunk(
  "schedule/updateSchedule",
  async (payload: IUpdateSchedulePayload, { rejectWithValue }) => {
    try {
      const meal = await scheduleAPI.updateSchedule(payload)
      return meal
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const deleteSchedule = createAppAsyncThunk(
  "schedule/deleteSchedule",
  async (scheduleId: string, { rejectWithValue }) => {
    try {
      await scheduleAPI.deleteSchedule(scheduleId)
      return scheduleId
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const updateScheduleEndDate = createAppAsyncThunk(
  "schedule/updateScheduleEndDate",
  async (
    { scheduleId, fromDate }: { scheduleId: string; fromDate: string },
    { rejectWithValue },
  ) => {
    try {
      await scheduleAPI.stopSchedule({ scheduleId, fromDate })
      return scheduleId
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

const initialState: IScheduleSliceState = {
  getSchedulesLoading: false,
  schedules: [],
  getSchedulesFailed: null,
  isNoSchedules: false,

  getScheduleDetailsLoading: false,
  scheduleDetails: undefined,
  getScheduleDetailsFailed: null,

  createScheduleLoading: false,
  createScheduleSuccess: undefined,
  createScheduleFailed: null,

  updateScheduleLoading: false,
  updateScheduleSuccess: undefined,
  updateScheduleFailed: null,

  deleteScheduleLoading: false,
  deleteScheduleSuccess: undefined,
  deleteScheduleFailed: null,

  updateScheduleEndDateLoading: false,
  updateScheduleEndDateSuccess: undefined,
  updateScheduleEndDateFailed: undefined,
}

export const scheduleSlice = createSlice({
  name: "schedule",
  initialState,
  reducers: {
    setScheduleDetails: (state, { payload }) => {
      state.scheduleDetails = payload
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getSchedules.pending, (state) => {
        state.getSchedulesLoading = true
        state.getSchedulesFailed = undefined
      })
      .addCase(getSchedules.fulfilled, (state, { payload }) => {
        state.getSchedulesLoading = false
        state.schedules = payload
        state.isNoSchedules = payload.length === 0
      })
      .addCase(getSchedules.rejected, (state, { payload }) => {
        state.getSchedulesLoading = true
        state.getSchedulesFailed = payload
      })

      .addCase(getScheduleDetails.pending, (state) => {
        state.getScheduleDetailsLoading = true
        state.scheduleDetails = undefined
        state.getScheduleDetailsFailed = undefined
      })
      .addCase(getScheduleDetails.fulfilled, (state, { payload }) => {
        state.getScheduleDetailsLoading = false
        state.scheduleDetails = payload
      })
      .addCase(getScheduleDetails.rejected, (state, { payload }) => {
        state.getScheduleDetailsLoading = false
        state.getScheduleDetailsFailed = payload
      })

      .addCase(createSchedule.pending, (state) => {
        state.createScheduleLoading = true
        state.createScheduleFailed = undefined
        state.createScheduleSuccess = undefined
      })
      .addCase(createSchedule.fulfilled, (state, { payload }) => {
        state.createScheduleLoading = false
        state.createScheduleSuccess = payload
      })
      .addCase(createSchedule.rejected, (state, { payload }) => {
        state.createScheduleLoading = false
        state.createScheduleFailed = payload
      })

      .addCase(updateSchedule.pending, (state) => {
        state.updateScheduleLoading = true
        state.updateScheduleFailed = undefined
        state.updateScheduleSuccess = undefined
      })
      .addCase(updateSchedule.fulfilled, (state, { payload }) => {
        state.updateScheduleLoading = false
        state.updateScheduleSuccess = payload
      })
      .addCase(updateSchedule.rejected, (state, { payload }) => {
        state.updateScheduleLoading = false
        state.updateScheduleFailed = payload
      })

      .addCase(deleteSchedule.pending, (state) => {
        state.deleteScheduleLoading = true
        state.deleteScheduleFailed = undefined
        state.deleteScheduleSuccess = undefined
      })
      .addCase(deleteSchedule.fulfilled, (state, { payload }) => {
        state.deleteScheduleLoading = false
        state.deleteScheduleSuccess = payload
        state.schedules = state.schedules.filter(({ id }) => id !== payload)
      })
      .addCase(deleteSchedule.rejected, (state, { payload }) => {
        state.deleteScheduleLoading = false
        state.deleteScheduleFailed = payload
      })

      .addCase(updateScheduleEndDate.pending, (state) => {
        state.updateScheduleEndDateLoading = true
        state.updateScheduleEndDateFailed = undefined
        state.updateScheduleEndDateSuccess = undefined
      })
      .addCase(updateScheduleEndDate.fulfilled, (state, { payload }) => {
        state.updateScheduleEndDateLoading = false
        state.updateScheduleEndDateSuccess = payload
      })
      .addCase(updateScheduleEndDate.rejected, (state, { payload }) => {
        state.updateScheduleEndDateLoading = false
        state.updateScheduleEndDateFailed = payload
      })

      .addCase(resetStoreAction, () => {
        return initialState
      })
  },
})

export const { setScheduleDetails } = scheduleSlice.actions

export default scheduleSlice.reducer
